import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2, Users } from 'lucide-react';
import api from '../../services/api';
import InviteCodeCell from '../admin/InviteCodeCell';
import OrgOperatorPanel from '../admin/OrgOperatorPanel';
import { OrgProvisionSuccessModal } from '../admin/OrgOperatorPanel';
import PhoneField from '../ui/PhoneField';
import '../d28d/d28d-admin-surfaces.css';

const emptyForm = () => ({
  nombre: '',
  email: '',
  telefono: '',
  especialidad: 'Entrenamiento personalizado',
  plan_id: 'Entrenadores Pro',
  copy_gallery_from_nico: true,
  copy_routines_from_nico: true,
  admin_password: '',
});

export default function CoachTrainersAdmin({ onBack = null }) {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [provisionModal, setProvisionModal] = useState(null);
  const [manageTrainerId, setManageTrainerId] = useState(null);
  const [coachPlans, setCoachPlans] = useState([]);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [editForm, setEditForm] = useState({ nombre: '', email: '', telefono: '', especialidad: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [usersTrainer, setUsersTrainer] = useState(null);
  const [usersRows, setUsersRows] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [trainersRes, plansRes] = await Promise.all([
        api.get('/trainers'),
        api.get('/accounts/plans', {
          params: { kind: 'training', training_audience: 'coach', visible: 'true' },
        }).catch(() => ({ data: [] })),
      ]);
      setTrainers(Array.isArray(trainersRes.data) ? trainersRes.data : (trainersRes.data?.data || []));
      const raw = plansRes.data;
      const allPlans = Array.isArray(raw)
        ? raw
        : (raw?.plans || raw?.data || []);
      setCoachPlans(
        allPlans.filter(
          (p) => String(p.kind) === 'training'
            && String(p.training_audience || 'coach') === 'coach'
            && p.activo !== false
            && p.visible !== false,
        ),
      );
    } catch {
      setError('No se pudo cargar entrenadores.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveInviteCode = async (trainerId, code) => {
    const { data } = await api.put(`/trainers/${trainerId}`, { invite_code: code });
    const updated = data.trainer || data;
    setTrainers((prev) => prev.map((t) => (t.id === trainerId ? { ...t, invite_code: updated.invite_code } : t)));
    return updated.invite_code;
  };

  const startEdit = (tr) => {
    setEditingTrainer(tr);
    setEditForm({
      nombre: tr.nombre || '',
      email: tr.email || '',
      telefono: tr.telefono || '',
      especialidad: tr.especialidad || '',
    });
    setError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingTrainer?.id) return;
    setEditSaving(true);
    setError('');
    try {
      await api.put(`/trainers/${editingTrainer.id}`, {
        nombre: editForm.nombre.trim(),
        email: editForm.email.trim().toLowerCase(),
        telefono: editForm.telefono?.trim() || null,
        especialidad: editForm.especialidad?.trim() || null,
      });
      setEditingTrainer(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar entrenador.');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteTrainer = async (tr) => {
    if (!window.confirm(`¿Eliminar al entrenador ${tr.nombre || ''}?`)) return;
    try {
      await api.delete(`/trainers/${tr.id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar entrenador.');
    }
  };

  const openUsers = async (tr) => {
    setUsersTrainer(tr);
    setUsersRows([]);
    setUsersError('');
    setUsersLoading(true);
    try {
      const { data } = await api.get(`/trainers/${tr.id}/users`);
      setUsersRows(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      setUsersError(err.response?.data?.error || 'No se pudieron cargar los usuarios.');
    } finally {
      setUsersLoading(false);
    }
  };

  const closeUsers = () => {
    setUsersTrainer(null);
    setUsersRows([]);
    setUsersError('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nombre?.trim() || !form.email?.trim()) {
      setError('Nombre y email son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/trainers', {
        nombre: form.nombre.trim(),
        email: form.email.trim().toLowerCase(),
        telefono: form.telefono?.trim() || null,
        especialidad: form.especialidad?.trim() || null,
        copy_gallery_from_nico: form.copy_gallery_from_nico === true,
        copy_routines_from_nico: form.copy_routines_from_nico === true,
        admin_password: form.admin_password?.trim() || undefined,
        plan_id: form.plan_id || 'Entrenadores Pro',
      });
      const body = res.data || {};
      setForm(emptyForm());
      await load();
      if (body.admin_credentials || body.admin_user || body.trainer?.invite_code || body.invite_code) {
        setProvisionModal({
          title: 'Empresa Training creada',
          inviteCode: body.invite_code || body.trainer?.invite_code,
          adminUser: body.admin_user,
          adminCredentials: body.admin_credentials,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear entrenador.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d28d-admin-surface surface-readable ui-light-panel dashboard-main-view">
      <header className="dashboard-header panel-admin-header">
        <div>
          {onBack && (
            <button type="button" className="btn btn-secondary panel-back-btn inline-flex items-center gap-2" onClick={onBack}>
              ← Panel Entrenadores
            </button>
          )}
          <h2 className="d28d-page-title">Admin de entrenadores</h2>
          <p className="d28d-text-muted">
            Crea coaches, asigna su código de vinculación y gestiona solo los usuarios que pertenecen a un entrenador.
          </p>
        </div>
      </header>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm">{error}</div>}

      <form onSubmit={handleCreate} className="card p-5 mb-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="label">Nombre</span>
          <input className="input mt-1 w-full" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Email</span>
          <input className="input mt-1 w-full" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <PhoneField
          label="Teléfono"
          value={form.telefono}
          onChange={(v) => setForm({ ...form, telefono: v })}
          inputClassName="input mt-1 w-full"
          className="block"
          labelClassName="label"
        />
        <label className="block">
          <span className="label">Especialidad</span>
          <input className="input mt-1 w-full" value={form.especialidad} onChange={(e) => setForm({ ...form, especialidad: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Plan activación clientes</span>
          <select
            className="input mt-1 w-full"
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            required
          >
            {(coachPlans.length ? coachPlans : [{ nombre: 'Entrenadores Pro', precio_mensual: 99000 }]).map((p) => (
              <option key={p.nombre} value={p.nombre}>
                {p.nombre} — ${Number(p.precio_mensual || 0).toLocaleString('es-CO')} COP / cliente
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">Contraseña login coach</span>
          <input
            className="input mt-1 w-full"
            type="text"
            value={form.admin_password}
            onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
            placeholder="Vacío = generar automática"
          />
        </label>
        <label className="flex items-center gap-2 md:col-span-2 lg:col-span-4 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.copy_gallery_from_nico !== false}
            onChange={(e) => setForm({ ...form, copy_gallery_from_nico: e.target.checked })}
          />
          Copiar galería de ejercicios de Nicolás del Río (plantilla con videos)
        </label>
        <label className="flex items-center gap-2 md:col-span-2 lg:col-span-4 text-sm text-stone-700">
          <input
            type="checkbox"
            checked={form.copy_routines_from_nico !== false}
            onChange={(e) => setForm({ ...form, copy_routines_from_nico: e.target.checked })}
          />
          Copiar rutinas plantilla de Nicolás del Río (catálogo privado editable)
        </label>
        <div className="md:col-span-2 lg:col-span-4">
          <button type="submit" className="btn btn-primary inline-flex items-center gap-2" disabled={saving}>
            {saving ? 'Guardando…' : '+ Crear entrenador'}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <h3 className="font-semibold text-stone-900">Entrenadores activos</h3>
        </div>
        {loading ? (
          <p className="p-6 text-stone-500 text-sm">Cargando…</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Marca blanca</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Plan clientes</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Código invitación</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">Admin</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {trainers.map((tr) => (
                  <tr key={tr.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-medium">{tr.nombre}</td>
                    <td className="px-4 py-3 text-stone-600">{tr.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${tr.white_label_enabled !== false ? 'bg-blue-100 text-blue-800' : 'bg-stone-100 text-stone-600'}`}>
                        {tr.white_label_enabled !== false ? 'Activa' : 'Off'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{tr.plan_id || 'Entrenadores Pro'}</td>
                    <td className="px-4 py-3">
                      <InviteCodeCell
                        value={tr.invite_code}
                        onSave={(code) => saveInviteCode(tr.id, code)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="btn btn-secondary text-xs inline-flex items-center gap-1"
                        onClick={() => setManageTrainerId(manageTrainerId === tr.id ? null : tr.id)}
                      >
                        {manageTrainerId === tr.id ? 'Cerrar' : 'Gestionar acceso'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="admin-row-actions admin-row-actions--table justify-end">
                        <button type="button" className="admin-action admin-action--neutral" onClick={() => openUsers(tr)} title="Usuarios">
                          <Users className="w-4 h-4" aria-hidden />
                          <span>Usuarios</span>
                        </button>
                        <button type="button" className="admin-action admin-action--edit" onClick={() => startEdit(tr)} title="Editar">
                          <Pencil className="w-4 h-4" aria-hidden />
                          <span>Editar</span>
                        </button>
                        <button type="button" className="admin-action admin-action--danger" onClick={() => deleteTrainer(tr)} title="Eliminar">
                          <Trash2 className="w-4 h-4" aria-hidden />
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manageTrainerId ? (
        <div className="card p-4 mt-4">
          <OrgOperatorPanel entityType="trainer" entityId={manageTrainerId} />
        </div>
      ) : null}

      {usersTrainer ? createPortal(
        <div className="form-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="trainer-users-title" onClick={closeUsers}>
          <div
            className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-2xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="trainer-users-title" className="text-lg font-semibold text-stone-900 mb-1">
              Usuarios de {usersTrainer.nombre || 'entrenador'}
            </h3>
            <p className="text-sm text-stone-600 mb-4">
              Clientes vinculados a este entrenador ({usersLoading ? '…' : usersRows.length}).
            </p>
            {usersError ? (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-3 text-sm">{usersError}</div>
            ) : null}
            {usersLoading ? (
              <p className="text-sm text-stone-500 py-6">Cargando…</p>
            ) : usersRows.length === 0 ? (
              <p className="text-sm text-stone-500 py-6">Este entrenador aún no tiene usuarios asignados.</p>
            ) : (
              <div className="overflow-x-auto max-h-[60vh] border border-stone-200 rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">Nombre</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">Teléfono</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-stone-500 uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersRows.map((u) => (
                      <tr key={u.id} className="border-t border-stone-100">
                        <td className="px-3 py-2 font-medium text-stone-900">{u.nombre || '—'}</td>
                        <td className="px-3 py-2 text-stone-600">{u.email || '—'}</td>
                        <td className="px-3 py-2 text-stone-600">{u.telefono || '—'}</td>
                        <td className="px-3 py-2 text-stone-600 text-xs">{u.estado || (u.activo === false ? 'Inactivo' : 'Activo')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end pt-4">
              <button type="button" className="btn btn-secondary" onClick={closeUsers} disabled={usersLoading}>
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {editingTrainer ? (
        <div className="form-modal-overlay" role="dialog" aria-modal="true" onClick={() => !editSaving && setEditingTrainer(null)}>
          <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-stone-900 mb-4">Editar entrenador</h3>
            <form onSubmit={saveEdit} className="space-y-3">
              <label className="block text-sm">
                <span className="font-semibold">Nombre</span>
                <input className="input mt-1 w-full" required value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
              </label>
              <label className="block text-sm">
                <span className="font-semibold">Email</span>
                <input className="input mt-1 w-full" type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </label>
              <PhoneField
                label="Teléfono"
                value={editForm.telefono}
                onChange={(v) => setEditForm({ ...editForm, telefono: v })}
                inputClassName="input mt-1"
              />
              <label className="block text-sm">
                <span className="font-semibold">Especialidad</span>
                <input className="input mt-1 w-full" value={editForm.especialidad} onChange={(e) => setEditForm({ ...editForm, especialidad: e.target.value })} />
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary" disabled={editSaving} onClick={() => setEditingTrainer(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={editSaving}>{editSaving ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <OrgProvisionSuccessModal
        open={Boolean(provisionModal)}
        onClose={() => setProvisionModal(null)}
        title={provisionModal?.title || ''}
        inviteCode={provisionModal?.inviteCode}
        adminUser={provisionModal?.adminUser}
        adminCredentials={provisionModal?.adminCredentials}
        entityLabel="empresa Training"
      />
    </div>
  );
}
